import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { usePartnerBranding } from "@/hooks/usePartnerBranding";
import ScriptInjector from "@/components/ScriptInjector";
import { ConsentBanner } from "@/components/ConsentBanner";
import EngagementTracker from "@/components/EngagementTracker";
import { AppLayout } from "@/components/app/AppLayout";
import { LayoutSwitch } from "@/components/app/layout-version/LayoutSwitch";
import { LayoutVersionProvider, useLayoutVersion } from "@/components/app/layout-version/LayoutVersionContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { BrandLoader } from "@/components/shared/BrandLoader";
import { useEffect, ReactNode, lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import { useMfaGate } from "@/hooks/useSecurityRequirements";

/* ── Lazy-loaded pages ───────────────────────────────────── */
const Index = lazy(() => import("./pages/Index"));
const Admin = lazy(() => import("./pages/Admin"));
const PartnerAdmin = lazy(() => import("./pages/PartnerAdmin"));
const Login = lazy(() => import("./pages/Login"));
const SignupWizard = lazy(() => import("./pages/SignupWizard"));
const Privacy = lazy(() => import("./pages/Privacy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Demo = lazy(() => import("./pages/Demo"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TR360V3 = lazy(() => import("./pages/TR360V3"));
const LandingV4 = lazy(() => import("./pages/LandingV4"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CustomResetPassword = lazy(() => import("./pages/CustomResetPassword"));
const NpsForm = lazy(() => import("./pages/NpsForm"));

/* ── App pages (authenticated) ───────────────────────────── */
const Dashboard = lazy(() => import("./pages/app").then(m => ({ default: m.Dashboard })));
const Executive = lazy(() => import("./pages/app/Executive"));
const Companies = lazy(() => import("./pages/app").then(m => ({ default: m.Companies })));
const Notifications = lazy(() => import("./pages/app/Notifications"));
const CompanyDetail = lazy(() => import("./pages/app/CompanyDetail"));
const SupplierQuestionnairePage = lazy(() => import("./pages/app/SupplierQuestionnairePage"));
const SupplierQuestionnaireResultPage = lazy(() => import("./pages/app/SupplierQuestionnaireResultPage"));
const BalanceUploadWizard = lazy(() => import("./pages/app/BalanceUploadWizard"));
const CompanyReport = lazy(() => import("./pages/app/CompanyReport"));
const CompanyReportPrint = lazy(() => import("./pages/app/CompanyReportPrint"));
const AIReportPrint = lazy(() => import("./pages/app/AIReportPrint"));
const RiskMap = lazy(() => import("./pages/app/RiskMap"));
const AddCompany = lazy(() => import("./pages/app/AddCompany"));
const Profile = lazy(() => import("./pages/app/Profile"));
const Settings = lazy(() => import("./pages/app/Settings"));
// Temporariamente desabilitado: import do CRM oculto enquanto a rota está comentada
// const CRM = lazy(() => import("./pages/app/CRM"));
const Billing = lazy(() => import("./pages/app/Billing"));
const SectorRiskDashboard = lazy(() => import("./pages/app/SectorRiskDashboard"));
const Reports = lazy(() => import("./pages/app/Reports"));
const Comparison = lazy(() => import("./pages/app/Comparison"));
const Terms = lazy(() => import("./pages/app/Terms"));
const Help = lazy(() => import("./pages/app/Help"));
const Collections = lazy(() => import("./pages/app/Collections"));
const CollectionWizard = lazy(() => import("./pages/app/CollectionWizard"));
const CollectionDetail = lazy(() => import("./pages/app/CollectionDetail"));
const CollectionOnboarding = lazy(() => import("./pages/app/CollectionOnboarding"));
const CollectionContract = lazy(() => import("./pages/app/CollectionContract"));
const Support = lazy(() => import("./pages/app/Support"));
const SupportTicketDetail = lazy(() => import("./pages/app/SupportTicketDetail"));
const Simulador360Home = lazy(() => import("./pages/app/simulador-360/Simulador360Home"));
const MatrizDependencia = lazy(() => import("./pages/app/simulador-360/MatrizDependencia"));
const Choque = lazy(() => import("./pages/app/simulador-360/Choque"));
const VisaoBrasil = lazy(() => import("./pages/app/simulador-360/VisaoBrasil"));
const Setores = lazy(() => import("./pages/app/simulador-360/Setores"));
const PortfolioUpdates = lazy(() => import("./pages/app/PortfolioUpdates"));

/* ── Layout legado do /app (src/legacy, congelado; docs/features/new-layout-flag) ── */
const LegacyAppLayout = lazy(() => import("@/legacy/components/app/AppLayout").then(m => ({ default: m.AppLayout })));
const LegacyDashboard = lazy(() => import("@/legacy/pages/app/Dashboard"));
const LegacyCompanies = lazy(() => import("@/legacy/pages/app/Companies"));
const LegacyAddCompany = lazy(() => import("@/legacy/pages/app/AddCompany"));
const LegacyCompanyDetail = lazy(() => import("@/legacy/pages/app/CompanyDetail"));
const LegacyCompanyReport = lazy(() => import("@/legacy/pages/app/CompanyReport"));
const LegacyAIReportPrint = lazy(() => import("@/legacy/pages/app/AIReportPrint"));
const LegacyCompanyReportPrint = lazy(() => import("@/legacy/pages/app/CompanyReportPrint"));
const LegacyRiskMap = lazy(() => import("@/legacy/pages/app/RiskMap"));
const LegacyPortfolioUpdates = lazy(() => import("@/legacy/pages/app/PortfolioUpdates"));
const LegacySectorRiskDashboard = lazy(() => import("@/legacy/pages/app/SectorRiskDashboard"));
const LegacyRatingComparison = lazy(() => import("@/legacy/pages/app/RatingComparison"));
const LegacyProfile = lazy(() => import("@/legacy/pages/app/Profile"));
const LegacyNotifications = lazy(() => import("@/legacy/pages/app/Notifications"));
const LegacyTerms = lazy(() => import("@/legacy/pages/app/Terms"));
const LegacySettings = lazy(() => import("@/legacy/pages/app/Settings"));
const LegacyBilling = lazy(() => import("@/legacy/pages/app/Billing"));
const LegacyReports = lazy(() => import("@/legacy/pages/app/Reports"));
const LegacyCollections = lazy(() => import("@/legacy/pages/app/Collections"));
const LegacyCollectionWizard = lazy(() => import("@/legacy/pages/app/CollectionWizard"));
const LegacyCollectionDetail = lazy(() => import("@/legacy/pages/app/CollectionDetail"));
const LegacyCollectionContract = lazy(() => import("@/legacy/pages/app/CollectionContract"));
const LegacyHelp = lazy(() => import("@/legacy/pages/app/Help"));
const LegacySupport = lazy(() => import("@/legacy/pages/app/Support"));
const LegacySupportTicketDetail = lazy(() => import("@/legacy/pages/app/SupportTicketDetail"));
const LegacyBuyers = lazy(() => import("@/legacy/pages/app/Buyers"));
const LegacySuppliers = lazy(() => import("@/legacy/pages/app/Suppliers"));
const LegacySimulador360Home = lazy(() => import("@/legacy/pages/app/simulador-360/Simulador360Home"));
const LegacyMatrizDependencia = lazy(() => import("@/legacy/pages/app/simulador-360/MatrizDependencia"));
const LegacyChoque = lazy(() => import("@/legacy/pages/app/simulador-360/Choque"));
const LegacyVisaoBrasil = lazy(() => import("@/legacy/pages/app/simulador-360/VisaoBrasil"));
const LegacySetores = lazy(() => import("@/legacy/pages/app/simulador-360/Setores"));
const MfaRequired = lazy(() => import("./pages/app/MfaRequired"));

const queryClient = new QueryClient();

// Protected Route Component for Admin
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isSuperAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <BrandLoader variant="fullscreen" />;
  }

  if (!user || !isSuperAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Protected Route Component for App
const REMIXICON_URL = "https://cdn.jsdelivr.net/npm/remixicon@4.6.0/fonts/remixicon.css";

const AppRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, tenantId, isSuperAdmin, isAdmin, needsTermsAcceptance, isDetailsLoaded } = useAuth();
  // Fase 2b (R1, C5): 2FA obrigatório por organização. Superadmin da plataforma isento; erro de leitura = fail-open.
  const mfaGate = useMfaGate(user?.id ?? null, !isLoading && isDetailsLoaded && !isSuperAdmin);

  // Load remixicon CSS only when inside the authenticated app
  React.useEffect(() => {
    if (document.querySelector(`link[href="${REMIXICON_URL}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = REMIXICON_URL;
    document.head.appendChild(link);
  }, []);

  if (isLoading) {
    return <BrandLoader variant="fullscreen" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user has no tenant and is not admin, redirect to pending state
  if (!tenantId && !isSuperAdmin && !isAdmin) {
    return <Navigate to="/login?mode=no-tenant" replace />;
  }

  // If user needs to accept terms, redirect to terms page
  if (needsTermsAcceptance) {
    return <Navigate to="/app/terms" replace />;
  }

  if (!isSuperAdmin) {
    // Status bloqueante em cache só vale depois de relido (evita voltar ao /login logo após verificar o 2FA).
    if (!isDetailsLoaded || mfaGate.isLoading || (mfaGate.isFetching && mfaGate.data !== "ok")) {
      return <BrandLoader variant="fullscreen" />;
    }
    if (mfaGate.data === "setup") {
      return <Navigate to="/app/mfa-required" replace />;
    }
    if (mfaGate.data === "verify") {
      // Fluxo de verificação existente (Login → MFAVerification), que volta para /app ao concluir.
      return <Navigate to="/login" replace />;
    }
  }

  return <VersionedAppLayout>{children}</VersionedAppLayout>;
};

/** Menu lateral e cabeçalho seguem a chave da organização (D3). */
const VersionedAppLayout = ({ children }: { children: React.ReactNode }) => {
  const version = useLayoutVersion();
  if (version === "loading") return <BrandLoader variant="fullscreen" />;
  return version === "new" ? <AppLayout>{children}</AppLayout> : <LegacyAppLayout>{children}</LegacyAppLayout>;
};

/** D6: rota só do layout novo dentro de /app/companies/:id → página da empresa no legado. */
const LegacyCompanyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/app/companies/${id}` : "/app/companies"} replace />;
};

// Route for Terms page (no layout, but requires auth)
const TermsRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <BrandLoader variant="fullscreen" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Permission-protected route wrapper
interface PermissionRouteProps {
  children: ReactNode;
  permissionKey: keyof ReturnType<typeof usePermissions>;
  fallbackPath?: string;
}

const PermissionRoute = ({ 
  children, 
  permissionKey, 
  fallbackPath = "/app" 
}: PermissionRouteProps) => {
  const permissions = usePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!permissions.isLoading && !permissions[permissionKey]) {
      navigate(fallbackPath, { replace: true });
    }
  }, [permissions.isLoading, permissions[permissionKey], navigate, fallbackPath]);

  if (permissions.isLoading) {
    return <BrandLoader variant="fullscreen" />;
  }

  if (!permissions[permissionKey]) {
    return null;
  }

  return <>{children}</>;
};

// Protected Route Component for Partner Admin
const PartnerRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, tenantId } = useAuth();
  const { isPartner, isLoading: isBrandingLoading } = usePartnerBranding();

  if (isLoading || isBrandingLoading) {
    return <BrandLoader variant="fullscreen" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is not a partner, redirect to regular app
  if (!isPartner) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<TR360V3 />} />
      <Route path="/lp-legacy" element={<Index />} />
      <Route path="/lp-nova" element={<LandingV4 />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset" element={<CustomResetPassword />} />
      <Route path="/auth" element={<Navigate to="/login" replace />} />
      <Route path="/signup" element={<SignupWizard />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/termos-de-uso" element={<TermsOfUse />} />
      <Route path="/politica-de-cookies" element={<Cookies />} />
      <Route path="/cookies" element={<Navigate to="/politica-de-cookies" replace />} />
      <Route path="/agendamento" element={<Demo />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/nps/:ticketId/:token" element={<NpsForm />} />
      {/* App Routes (Client Dashboard) */}
      {/* Dashboard - requires canViewDashboard */}
      <Route path="/app" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewDashboard">
            <LayoutSwitch next={<Dashboard />} legacy={<LegacyDashboard />} />
          </PermissionRoute>
        </AppRoute>
      } />

      {/* Executive - visão executiva de originação/aprovação (protótipo Figma) */}
      <Route path="/app/executive" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewDashboard">
            <LayoutSwitch next={<Executive />} legacy={<Navigate to="/app" replace />} />
          </PermissionRoute>
        </AppRoute>
      } />

      {/* Companies routes - require canViewCompanies */}
      <Route path="/app/companies" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <LayoutSwitch next={<Companies />} legacy={<LegacyCompanies />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/companies/new" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <LayoutSwitch next={<AddCompany />} legacy={<LegacyAddCompany />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/companies/:id" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<CompanyDetail />} legacy={<LegacyCompanyDetail />} />
            </ErrorBoundary>
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/companies/:id/questionario" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<SupplierQuestionnairePage />} legacy={<LegacyCompanyRedirect />} />
            </ErrorBoundary>
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/companies/:id/questionario/resultado" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<SupplierQuestionnaireResultPage />} legacy={<LegacyCompanyRedirect />} />
            </ErrorBoundary>
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/companies/:id/balanco/enviar" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<BalanceUploadWizard />} legacy={<LegacyCompanyRedirect />} />
            </ErrorBoundary>
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/companies/:id/report" element={
        <TermsRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <LayoutSwitch next={<CompanyReport />} legacy={<LegacyCompanyReport />} />
          </PermissionRoute>
        </TermsRoute>
      } />
      <Route path="/app/companies/:id/ai-report-print" element={
        <TermsRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<AIReportPrint />} legacy={<LegacyAIReportPrint />} />
            </ErrorBoundary>
          </PermissionRoute>
        </TermsRoute>
      } />
      <Route path="/app/companies/:id/report-print-br" element={
        <TermsRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<CompanyReportPrint expectedScope="BR" />} legacy={<LegacyCompanyReportPrint expectedScope="BR" />} />
            </ErrorBoundary>
          </PermissionRoute>
        </TermsRoute>
      } />
      <Route path="/app/companies/:id/report-print-gb" element={
        <TermsRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <ErrorBoundary>
              <LayoutSwitch next={<CompanyReportPrint expectedScope="INTL" />} legacy={<LegacyCompanyReportPrint expectedScope="INTL" />} />
            </ErrorBoundary>
          </PermissionRoute>
        </TermsRoute>
      } />
      <Route path="/admin/empresas/:id/report-print-br" element={
        <AdminRoute>
          <ErrorBoundary>
            <CompanyReportPrint expectedScope="BR" basePath="/admin/empresas" />
          </ErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/admin/empresas/:id/report-print-gb" element={
        <AdminRoute>
          <ErrorBoundary>
            <CompanyReportPrint expectedScope="INTL" basePath="/admin/empresas" />
          </ErrorBoundary>
        </AdminRoute>
      } />
      <Route path="/app/buyers" element={
        <LayoutSwitch
          next={<Navigate to="/app/companies?type=Buyer" replace />}
          legacy={
            <AppRoute>
              <PermissionRoute permissionKey="canViewCompanies">
                <LegacyBuyers />
              </PermissionRoute>
            </AppRoute>
          }
        />
      } />
      <Route path="/app/suppliers" element={
        <LayoutSwitch
          next={<Navigate to="/app/companies?type=Supplier" replace />}
          legacy={
            <AppRoute>
              <PermissionRoute permissionKey="canViewCompanies">
                <LegacySuppliers />
              </PermissionRoute>
            </AppRoute>
          }
        />
      } />
      <Route path="/app/risk-map" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <LayoutSwitch next={<RiskMap />} legacy={<LegacyRiskMap />} />
          </PermissionRoute>
        </AppRoute>
      } />
      
      <Route path="/app/portfolio-updates" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCompanies">
            <LayoutSwitch next={<PortfolioUpdates />} legacy={<LegacyPortfolioUpdates />} />
          </PermissionRoute>
        </AppRoute>
      } />

      {/* Dashboard related routes */}
      <Route path="/app/sector-risk" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewDashboard">
            <LayoutSwitch next={<SectorRiskDashboard />} legacy={<LegacySectorRiskDashboard />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/comparison" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewDashboard">
            <PermissionRoute permissionKey="canViewCompanies">
              <LayoutSwitch next={<Comparison />} legacy={<LegacyRatingComparison />} />
            </PermissionRoute>
          </PermissionRoute>
        </AppRoute>
      } />
      
      {/* Simulador 360 */}
      <Route path="/app/simulador-360" element={
        <AppRoute><PermissionRoute permissionKey="canViewDashboard">
          <LayoutSwitch next={<Simulador360Home />} legacy={<LegacySimulador360Home />} />
        </PermissionRoute></AppRoute>
      } />
      <Route path="/app/simulador-360/matriz-dependencia" element={
        <AppRoute><PermissionRoute permissionKey="canViewDashboard">
          <LayoutSwitch next={<MatrizDependencia />} legacy={<LegacyMatrizDependencia />} />
        </PermissionRoute></AppRoute>
      } />
      <Route path="/app/simulador-360/choque" element={
        <AppRoute><PermissionRoute permissionKey="canViewDashboard">
          <LayoutSwitch next={<Choque />} legacy={<LegacyChoque />} />
        </PermissionRoute></AppRoute>
      } />
      <Route path="/app/simulador-360/brasil" element={
        <AppRoute><PermissionRoute permissionKey="canViewDashboard">
          <LayoutSwitch next={<VisaoBrasil />} legacy={<LegacyVisaoBrasil />} />
        </PermissionRoute></AppRoute>
      } />
      <Route path="/app/simulador-360/setores" element={
        <AppRoute><PermissionRoute permissionKey="canViewDashboard">
          <LayoutSwitch next={<Setores />} legacy={<LegacySetores />} />
        </PermissionRoute></AppRoute>
      } />

      {/* Profile - always accessible */}
      <Route path="/app/profile" element={<AppRoute><LayoutSwitch next={<Profile />} legacy={<LegacyProfile />} /></AppRoute>} />
      <Route path="/app/notifications" element={<AppRoute><LayoutSwitch next={<Notifications />} legacy={<LegacyNotifications />} /></AppRoute>} />
      
      {/* Terms of Use - requires auth but no layout */}
      <Route path="/app/terms" element={<TermsRoute><LayoutSwitch next={<Terms />} legacy={<LegacyTerms />} /></TermsRoute>} />

      {/* 2FA obrigatório pela organização - requires auth but no layout */}
      <Route path="/app/mfa-required" element={<TermsRoute><MfaRequired /></TermsRoute>} />
      
      {/* Settings - requires canViewSettings */}
      <Route path="/app/settings" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewSettings">
            <LayoutSwitch next={<Settings />} legacy={<LegacySettings />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/settings/team" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewSettings">
            <LayoutSwitch next={<Settings tab="team" />} legacy={<Navigate to="/app/settings" replace />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/settings/notifications" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewSettings">
            <LayoutSwitch next={<Settings tab="notifications" />} legacy={<Navigate to="/app/settings" replace />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/settings/integrations" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewSettings">
            <LayoutSwitch next={<Settings tab="integrations" />} legacy={<Navigate to="/app/settings" replace />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/settings/security" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewSettings">
            <LayoutSwitch next={<Settings tab="security" />} legacy={<Navigate to="/app/settings" replace />} />
          </PermissionRoute>
        </AppRoute>
      } />
      
      {/* CRM - requires canExportData */}
      {/* Temporariamente desabilitado: rota CRM Exportação oculta para admin/colaboradores
      <Route path="/app/crm" element={
        <AppRoute>
          <PermissionRoute permissionKey="canExportData">
            <CRM />
          </PermissionRoute>
        </AppRoute>
      } />
      */}
      
      {/* Billing - requires canViewBilling */}
      <Route path="/app/billing" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewBilling">
            <LayoutSwitch next={<Billing />} legacy={<LegacyBilling />} />
          </PermissionRoute>
        </AppRoute>
      } />
      
      {/* Reports - requires canViewReports */}
      <Route path="/app/reports" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewReports">
            <LayoutSwitch next={<Reports />} legacy={<LegacyReports />} />
          </PermissionRoute>
        </AppRoute>
      } />

      {/* Collections (Cobranças) */}
      <Route path="/app/collections" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCollections">
            <LayoutSwitch next={<Collections />} legacy={<LegacyCollections />} />
          </PermissionRoute>
        </AppRoute>
      } />
      <Route path="/app/collections/new" element={
        <AppRoute>
          <LayoutSwitch next={<CollectionWizard />} legacy={<LegacyCollectionWizard />} />
        </AppRoute>
      } />
      <Route path="/app/collections/onboarding" element={
        <AppRoute>
          <CollectionOnboarding />
        </AppRoute>
      } />
      <Route path="/app/collections/contract" element={
        <AppRoute>
          <LayoutSwitch next={<CollectionContract />} legacy={<LegacyCollectionContract />} />
        </AppRoute>
      } />
      <Route path="/app/collections/:id" element={
        <AppRoute>
          <PermissionRoute permissionKey="canViewCollections">
            <LayoutSwitch next={<CollectionDetail />} legacy={<LegacyCollectionDetail />} />
          </PermissionRoute>
        </AppRoute>
      } />

      {/* Help - always accessible */}
      <Route path="/app/help" element={<AppRoute><LayoutSwitch next={<Help />} legacy={<LegacyHelp />} /></AppRoute>} />

      {/* Support - always accessible */}
      <Route path="/app/support" element={<AppRoute><LayoutSwitch next={<Support />} legacy={<LegacySupport />} /></AppRoute>} />
      <Route path="/app/support/:ticketId" element={<AppRoute><LayoutSwitch next={<SupportTicketDetail />} legacy={<LegacySupportTicketDetail />} /></AppRoute>} />

      {/* Admin Routes */}
      <Route 
        path="/admin/*" 
        element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        } 
      />

      {/* Partner Admin Routes */}
      <Route 
        path="/partner/*" 
        element={
          <PartnerRoute>
            <PartnerAdmin />
          </PartnerRoute>
        } 
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ConsentProvider>
                  <ScriptInjector />
                  <EngagementTracker />
                  <ConsentBanner />
                  <ErrorBoundary>
                    <Suspense fallback={<BrandLoader variant="fullscreen" />}>
                      <LayoutVersionProvider>
                        <AppRoutes />
                      </LayoutVersionProvider>
                    </Suspense>
                  </ErrorBoundary>
                </ConsentProvider>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
